/**
 *
 * ApiLogs
 *
 */
import React, {
  useState,
  memo,
  useEffect,
  SyntheticEvent,
  useCallback,
} from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { compose } from 'redux';
import { useSearchParams } from 'react-router-dom';
import {
  Dropdown,
  Button,
  Conditional,
  Space,
  Text,
  toast,
  AlertModal,
  Cross
} from '@cashfree-intl/coherent';

// Selectors
import makeSelectWhitelisting from './selectors';

// Reducers
import reducer from './reducer';

// Sagas
import saga from './saga';

// Actions
import {
  fetchWhitelistingAction,
  addWhitelistingAction,
  checkLivenessAction,
  fetchWhitelistingDetailsAction,
  updateWhitelistingAction,
} from './actions';

// Constants
import {
  TYPE_DROPDOWN_OPTIONS,
  DOMAIN_REF,
  PACKAGE_REF,
  EMPTY_DROPDOWN_STATE,
  REJECTION_STATUSES,
  PAGES,
  MODALS,
} from './constants';
import { SOURCE_TYPE } from 'utils/constants';

// Hooks
import { useAuth } from 'hooks/useAuth';
import { useInjectSaga } from 'utils/injectSaga';
import { useInjectReducer } from 'utils/injectReducer';

// Styled
import {
  BottomActionBar,
  StyledAddWhitelistingDropdownWrapper,
  StyledWebsiteDetailsWrapper,
} from './styled';

// Utils
import { formatWhitelistingDetails, createTableHeadings } from './utils';

// Containers
import WebsiteDetails from 'containers/WebsiteDetails';

// Components
import BackButton from 'components/BackButton';
import PaginatedTable from 'components/PaginatedTable';
import AddToWhitelistModal from './components/AddToWhitelistModal';
import InactiveWebsiteModal from './components/InactiveWebsiteModal';
import ReviewModal from './components/ReviewModal';
import Loader from 'components/Loader';

// Helpers
import { getNoRecordsObj, getTableData } from './helpers';
import { formatColumns } from 'components/PaginatedTable/helpers';

// Types
import {
  AddToWhitelistResponseType,
  OpenInactiveModalDataType,
  WhitelistingProps,
  WhitelistTableItemType,
  WhitelistTableResponseType,
  WhitlistingIssueDetailsType,
} from './types';

export function Whitelisting({
  whitelisting,
  fetchWhitelisting,
  addWhitelisting,
  updateWhitelisting,
  checkLiveness,
  fetchWhitelistingDetails,
  showFromShopify,
  onCloseShopifyModal
}: WhitelistingProps) {
  useInjectReducer({ key: 'whitelisting', reducer });
  useInjectSaga({ key: 'whitelisting', saga, mode: undefined });
  const auth = useAuth();
  const [searchParams] = useSearchParams();

  const [type, setType] = useState(EMPTY_DROPDOWN_STATE);
  const [domains, setDomains] = useState<WhitelistTableResponseType>([]);
  const [whitelistingIssueDetails, setWhitelistingIssueDetails] = useState<
    WhitlistingIssueDetailsType
  >({
    rejectedKeys: [],
    featureToggleMap: {},
    onboardingDetails: {
      status: {},
      locked: {},
      rejectionReasons: {},
      typeIdMap: {},
    },
  });
  const [showActions, setShowActions] = useState(false);
  const [modalName, setModalName] = useState('');
  const [page, setActivePage] = useState<PAGES>(PAGES.WHITELISTING);
  const [inactiveModalData, setInactiveModalData] = useState({
    domainUrl: '',
  });
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);

  useEffect(() => {
    if (page === PAGES.WHITELISTING) dataRetrieve();
  }, [searchParams.get('env'), page]);

  useEffect(() => {
    if (!isSubmitDisabled) toast.success('Please submit for review');
  }, [isSubmitDisabled]);

  const onModalClose = () => {
    setModalName('');
    setType(EMPTY_DROPDOWN_STATE);
  };

  const onReviewConfirm = () => {
    onModalClose();
    dataRetrieve();
  };

  const openReviewModal = (
    res?: AddToWhitelistResponseType,
    isWebsite?: boolean,
  ) => {
    if (res && !res.isAutoApproved) {
      if (isWebsite) setModalName(MODALS.REVIEW_DOMAIN);
      else setModalName(MODALS.REVIEW_APP);
    } else if (isWebsite) setModalName(MODALS.APPROVED_DOMAIN);
    else setModalName(MODALS.APPROVED_APP);
  };

  const openInactiveModal = (data: OpenInactiveModalDataType) => {
    setInactiveModalData(data);
    setModalName(MODALS.INACTIVE_DOMAIN);
  };
  const typeChange = (_e: SyntheticEvent, data) => {
    setModalName(MODALS.ADD_TO_WHITELIST);
    if (data.value === DOMAIN_REF) {
      setType({
        refName: 'Domain Name',
        refType: DOMAIN_REF,
      });
    } else {
      setType({
        refName: 'App Package',
        refType: PACKAGE_REF,
      });
    }
  };

  const dataRetrieve = () => {
    fetchWhitelisting({
      successCb: response => {
        const validDomains = response.filter(item =>
          [DOMAIN_REF, PACKAGE_REF].includes(item.referenceType),
        );
        if (
          validDomains.some(domain =>
            REJECTION_STATUSES.includes(domain.ticketStatus),
          )
        )
          setShowActions(true);
        setDomains(validDomains);
      },
    });
  };

  const onActionClick = (rowData: WhitelistTableItemType) => () => {
    const { version, ticketStatus } = rowData;
    fetchWhitelistingDetails({
      reqData: {
        version,
      },
      successCb: response => {
        setWhitelistingIssueDetails(
          formatWhitelistingDetails(response, ticketStatus),
        );
        setActivePage(PAGES.FIX_ERROR);
      },
    });
  };
  const onWhitelistingSubmitClick = () => {
    updateWhitelisting({
      reqData: {
        sourceType: SOURCE_TYPE.DASHBOARD,
        version: whitelistingIssueDetails.version,
        ticketStatus: 'MERCHANT',
      },
      successCb: navigateToWhitelisting,
    });
  };

  const navigateToWhitelisting = () => setActivePage(PAGES.WHITELISTING);

  const { featureToggleMap, rejectedKeys } = whitelistingIssueDetails;

  const actions = useCallback(
    (_: boolean, hasChatsMap: Record<string, boolean>) => {
      const isButtonDisabled = !rejectedKeys.every(
        regKey => !!hasChatsMap && hasChatsMap[regKey],
      );
      if (isSubmitDisabled !== isButtonDisabled)
        setIsSubmitDisabled(isButtonDisabled);
      return (
        <BottomActionBar>
          <Space justifyContent="flex-end">
            <Button link onClick={navigateToWhitelisting}>
              Cancel
            </Button>
            <Button
              primary
              disabled={isButtonDisabled}
              onClick={onWhitelistingSubmitClick}
            >
              Submit for review
            </Button>
          </Space>
        </BottomActionBar>
      );
    },
    [isSubmitDisabled, rejectedKeys],
  );

  const { loaders } = whitelisting;
  const tableNotFoundData = getNoRecordsObj(domains, loaders.PAGE_LOADER);
  const updatedTableData = getTableData(
    domains,
    showActions,
    loaders.PAGE_LOADER,
    onActionClick,
  );
  const tableHeadings = formatColumns(createTableHeadings(showActions));
  const issuesPageHeader = featureToggleMap.comments
    ? 'Fix the following issues'
    : 'Rejection Reasons';
  return (
    <div className="whitelisting-container">
      <Loader active={loaders.PAGE_LOADER} />
      <Conditional if={page === PAGES.WHITELISTING}>
        <Conditional if={showFromShopify}>
          <Space gap={2} alignItems="flex-start" justifyContent="space-between">
            <Text variant="h20">Whitelisting</Text>
            <Cross onClick={onCloseShopifyModal} />
          </Space>
        </Conditional>
        <Conditional if={!showFromShopify}>
          <Text variant="h20">Whitelisting</Text>
        </Conditional>
        <Text variant="b14" color="bodyLight" className="mb-4">
          View, add and track your whitelisting request
        </Text>
        <Conditional if={modalName === MODALS.ADD_TO_WHITELIST}>
          <AddToWhitelistModal
            openInactiveModal={openInactiveModal}
            onModalClose={onModalClose}
            openReviewModal={openReviewModal}
            type={type.refType}
            addWhitelisting={addWhitelisting}
            checkLiveness={checkLiveness}
            isLoading={loaders.MODAL_LOADER}
          />
        </Conditional>
        <Conditional if={modalName === MODALS.INACTIVE_DOMAIN}>
          <InactiveWebsiteModal
            openReviewModal={openReviewModal}
            onModalClose={onModalClose}
            modalData={inactiveModalData}
            addWhitelisting={addWhitelisting}
            isLoading={loaders.MODAL_LOADER}
          />
        </Conditional>
        <Conditional
          if={
            modalName === MODALS.REVIEW_APP ||
            modalName === MODALS.REVIEW_DOMAIN
          }
        >
          <ReviewModal
            onConfirm={onReviewConfirm}
            isLoading={loaders.MODAL_LOADER}
            isWebsite={modalName === MODALS.REVIEW_DOMAIN}
          />
        </Conditional>
        <Conditional
          if={
            modalName === MODALS.APPROVED_APP ||
            modalName === MODALS.APPROVED_DOMAIN
          }
        >
          <AlertModal
            type="success"
            title={`Awesome! Your ${modalName === MODALS.APPROVED_DOMAIN ? 'website' : 'app'
              } is now whitelisted!`}
            closeText="Ok, Got it"
            onClose={onReviewConfirm}
          />
        </Conditional>
        <StyledAddWhitelistingDropdownWrapper justifyContent="flex-end">
          <Dropdown
            button
            $buttonVariant="primary"
            icon="chevron down"
            text="Add New"
            options={TYPE_DROPDOWN_OPTIONS}
            onChange={typeChange}
            value={type.refType}
            selectOnBlur={false}
            data-event-name="Primary_Button_AddNew"
            selection
          />
        </StyledAddWhitelistingDropdownWrapper>
        <PaginatedTable
          data={updatedTableData}
          tableHeading={tableHeadings}
          emptyTableComponent={tableNotFoundData as any}
          fetching={loaders.PAGE_LOADER}
          isPaginated={false}
        />
      </Conditional>
      <Conditional if={page === PAGES.FIX_ERROR}>
        <StyledWebsiteDetailsWrapper>
          <Text variant="h20" className="mb-4">
            {issuesPageHeader}
          </Text>
          <BackButton onClick={navigateToWhitelisting} className="mb-5" />
          <WebsiteDetails
            auth={auth}
            keyType="secondaryDomainWhitelisting"
            {...whitelistingIssueDetails}
            sourceType={SOURCE_TYPE.DASHBOARD}
            actions={featureToggleMap.comments && actions}
            isRemote={true}
          />
        </StyledWebsiteDetailsWrapper>
      </Conditional>
    </div>
  );
}

const mapStateToProps = createStructuredSelector({
  whitelisting: makeSelectWhitelisting(),
});

function mapDispatchToProps(dispatch) {
  return {
    fetchWhitelisting: data => dispatch(fetchWhitelistingAction(data)),
    addWhitelisting: data => dispatch(addWhitelistingAction(data)),
    checkLiveness: data => dispatch(checkLivenessAction(data)),
    fetchWhitelistingDetails: data =>
      dispatch(fetchWhitelistingDetailsAction(data)),
    updateWhitelisting: data => dispatch(updateWhitelistingAction(data)),
  };
}
const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);
export default compose(
  withConnect,
  memo,
)(Whitelisting);
